import { ChangeEvent, FC, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TextField } from '@mui/material';

import Modal from '@components/Modal/Modal';
import { userEmailSelector } from '@store/features/auth/user/selectors';
import { handleSubmitInquiry } from '@store/features/inquiryDraft/submitInquiry/actions';
import { submitInquiryIsLoadingSelector } from '@store/features/inquiryDraft/submitInquiry/selectors';
import useDispatch from '@store/hooks/useDispatch';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import { ISubmitInquiryModalProps } from './SubmitInquiryModal.interface';

import './style.scss';

const SubmitInquiryModal: FC<ISubmitInquiryModalProps> = ({
  open,
  inquiryNumber,
  submitButtonTitle = '',
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const confirmationEmail = useSelector(userEmailSelector);
  const isSubmitting = useSelector(submitInquiryIsLoadingSelector);

  const [inquiryName, setInquiryName] = useState<string>();
  const [isInquiryNameError, setIsInquiryNameError] = useState(false);

  const handleChangeInquiryName = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      isInquiryNameError && setIsInquiryNameError(false);
      setInquiryName(event.target.value);
    },
    [isInquiryNameError],
  );

  const handleSubmit = useCallback(() => {
    dispatch(handleSubmitInquiry({ inquiryId: inquiryNumber, inquiryName }));
  }, [dispatch, inquiryName, inquiryNumber]);

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={t('submitInquiry.title')}
      className="submit-inquiry-modal"
      rightActionTitle={submitButtonTitle}
      rightActionIcon={<img src="/images/img-submit.svg" alt="Submit button" />}
      onRightAction={handleSubmit}
      isRightActionDisabled={isSubmitting}
    >
      <p className="submit-inquiry-modal__inquiry-number">
        {t('submitInquiry.inquiry')} #{inquiryNumber}
      </p>

      <TextField
        label={t('submitInquiry.inquiryNameLabel')}
        className="submit-inquiry-modal__inquiry-name-input"
        variant="filled"
        error={isInquiryNameError}
        helperText={isInquiryNameError && t('common.required')}
        multiline
        maxRows={2}
        value={inquiryName}
        onChange={handleChangeInquiryName}
        onBlur={e => setInquiryName(removeWhiteSpaces(e.target.value))}
      />

      <p className="submit-inquiry-modal__confirmation-footnote">
        <Trans values={{ email: confirmationEmail }}>{t('submitInquiry.confirmationText')}</Trans>
      </p>
    </Modal>
  );
};

export default SubmitInquiryModal;
